import { useMemo, useState } from 'react'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from '../../../components/DataTable'
import { DataTableContainer } from '../../../components/DataTable/UI'
import { IColumnVisibility } from '../../../components/DataTable/types'
import useApiService from '../../../services/api'
import { ColumnsStoreProvider } from '../../../stores/ColumnsStore/ColumnsStoreProvider'
import { OfferUserDetailsModal } from '../OfferUserDetailsModal'
import * as S from '../../../components/Tables/CommonTable.styles'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { TableLinkStyleProvider } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterIdentifier } from 'models/saved_view'
import { cn } from 'components/UI/cn'
import { TableSearch } from 'components/Filters/TableSearch'
import { useRebateEnrollmentsColumns } from './rebateEnrollmentsColumns'
import { RebateOfferEnrollmentsFilterset } from './RebateOfferEnrollmentsFilterset'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { getTableQueryKey } from 'utils/getTableQueryKey'
import { useQuery } from '@tanstack/react-query'
import EnrollmentExportModal from 'components/Modals/ExportModal/EnrollmentExportModal'
import { ExportButton } from 'components/Buttons/ExportButton'
import { BsDownload } from 'react-icons/bs'

const PAGE_SIZE = 100

function RebateOfferEnrollmentsTabComponent({
  offerId,
  tableKey,
}: {
  offerId: number
  tableKey: string
}) {
  const api = useApiService()
  const [selectedAssociationId, setSelectedAssociationId] = useState<
    number | null
  >(null)
  const IDENTIFIER = 'REBATE_ENROLLMENTS_TABLE'
  const filterStore = filterStoreRepo.getStore(IDENTIFIER as FilterIdentifier)
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()
  const { page, setPage } = filterStore()

  const {
    methods: { clearSelectedRows },
  } = useDataTableContext()

  const filterParams = useFilterParams(filterStore)

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filterParams,
      ...sortParams,
    }),
    [filterParams, sortParams]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data } = useQuery({
    staleTime: Infinity,
    queryKey: TABLE_QUERY_KEY,
    queryFn: async () => {
      clearSelectedRows()
      return api.getRebateEnrollments(offerId, {
        page: page,
        limit: PAGE_SIZE,
        ...filterAndSortParams,
      })
    },
  })
  // END DATA FETCHING

  const columns = useRebateEnrollmentsColumns()

  const [showExportModal, setShowExportModal] = useState(false)

  return (
    <>
      <TableLinkStyleProvider variant="plain">
        <DataTableContainer>
          <EnrollmentExportModal
            show={showExportModal}
            _handleClose={() => setShowExportModal(false)}
            filters={filterAndSortParams}
            offerId={offerId}
          />
          <DataTable
            tableHeader={
              <S.SearchContainer>
                <div className="flex w-full gap-2 items-center">
                  <TableSearch
                    filterStore={filterStore}
                    className="flex-1"
                    searchPlaceholder="Search by name, email, and company name"
                    tableKey={tableKey}
                  />
                  <RebateOfferEnrollmentsFilterset
                    filterIdentifier={IDENTIFIER}
                  />
                  <ColumnSelectorRecipient tableKey={tableKey} />
                </div>

                <div className={cn('flex flex-row justify-between gap-4')}>
                  <FilterChips
                    identifier={IDENTIFIER as FilterIdentifier}
                    store={filterStore}
                    showActive
                    clearAllButton
                  />
                </div>
              </S.SearchContainer>
            }
            loading={isFetching}
            data={data?.results ?? []}
            columns={columns}
            tableKey={tableKey}
            enableColumnResizing
            rowHeight={44}
            headerHeight={40}
            sortableFields="all"
            onRowClick={(row) => setSelectedAssociationId(row.original.id)}
            paginationOptions={{
              totalRows: data?.count ?? 0,
              pageSize: PAGE_SIZE,
              setPage: setPage,
              page: page,
              isPaginationLoading: isFetching,
            }}
            footerControls={
              <ExportButton
                variant="secondary"
                tooltip="Download limited to 10,000 rows"
                onClick={() => setShowExportModal(true)}
              >
                <BsDownload size={18} />
              </ExportButton>
            }
          />
        </DataTableContainer>
      </TableLinkStyleProvider>

      {selectedAssociationId && (
        <OfferUserDetailsModal
          open={true}
          onClose={() => setSelectedAssociationId(null)}
          associationId={selectedAssociationId}
        />
      )}
    </>
  )
}

export function RebateOfferEnrollmentsTab({
  offerId,
  defaultColumnVisibility,
  tableKey,
}: {
  offerId: number
  defaultColumnVisibility?: IColumnVisibility<any>
  tableKey: string
}) {
  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
    >
      <DataTableProvider tableKey={tableKey}>
        <RebateOfferEnrollmentsTabComponent
          offerId={offerId}
          tableKey={tableKey}
        />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
